import { useEffect } from "react";
import { CheckCircle2, CalendarCheck, MessageSquareText, Route as RouteIcon, ShieldAlert, ArrowRight, Mail, Gift } from "lucide-react";

const C = { navy: "#001540", blue: "#0a2259", gold: "#f5c84c", green: "#a7cb90", bg: "#f6f7fb", text: "#1b2333" };
const FORM_ID = "BwaGlUBraiOT9fWdo8N9";
export const MIAMI_BONUS = {
  short: "REGALO 3 días y 2 noches en Miami Beach",
  title: "REGALO 3 días y 2 noches en Miami Beach para 2 personas GRATIS",
  img: "https://images.pexels.com/photos/33416029/pexels-photo-33416029.jpeg?auto=compress&cs=tinysrgb&w=1600",
};

const ECUADOR = {
  bonus: MIAMI_BONUS,
  path: "/ecuador",
  demonym: "ecuatorianos",
  city: "cualquier ciudad del Ecuador",
  heroImg: "https://images.unsplash.com/photo-1655722725332-9925c96dd627?crop=entropy&cs=srgb&fm=jpg&q=80&w=1920",
  heroAlt: "Pasaporte con sellos de viaje",
  formName: "Asesoría visa Ecuador",
};

const steps = (city) => [
  { icon: CalendarCheck, title: "Agenda tu reunión", desc: `Completa el formulario y elige el horario que mejor te acomode. La reunión es por videollamada, desde ${city}.` },
  { icon: MessageSquareText, title: "Revisamos tu perfil", desc: "En 20 minutos analizamos tu situación laboral, familiar, económica y tu historial de viajes para identificar fortalezas y riesgos." },
  { icon: RouteIcon, title: "Recibes tu plan de acción", desc: "Te explicamos qué visa te conviene, qué documentos preparar y cuáles son los siguientes pasos para presentar una solicitud sólida." },
];

const DESTINATIONS = [
  { name: "Estados Unidos", visa: "Visa de turismo y negocios B1/B2", desc: "Preparación del formulario DS-160, orientación para la entrevista consular y revisión de tu perfil.", img: "https://images.unsplash.com/photo-1496588152823-86ff7695e68f?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" },
  { name: "Canadá", visa: "Visa de visitante (TRV)", desc: "Armado del expediente, cartas de soporte y pruebas de arraigo para una solicitud en línea ordenada.", img: "https://images.unsplash.com/photo-1517935706615-2717063c2225?crop=entropy&cs=srgb&fm=jpg&q=80&w=900" },
  { name: "México", visa: "Visa de visitante", desc: "Requisitos actualizados, solvencia económica y preparación para tu cita en el consulado.", img: "https://images.unsplash.com/photo-1561500880-dbf9ab090b79?crop=entropy&cs=srgb&fm=jpg&q=80&w=900", pos: "center 20%" },
];

const REASONS = [
  "Asesoría personalizada: cada perfil es distinto y lo tratamos así.",
  "Te decimos con honestidad si es buen momento para aplicar o si conviene fortalecer tu perfil primero.",
  "Acompañamiento con tus formularios y documentos para evitar errores que cuestan una negación.",
  "Simulación de entrevista para que llegues seguro y con respuestas claras.",
  "Atención en español, por videollamada y WhatsApp, sin moverte de tu ciudad.",
];

const scrollToForm = () => document.getElementById("ec-formulario")?.scrollIntoView({ behavior: "smooth", block: "start" });

const usePageSetup = (cfg) => {
  useEffect(() => {
    const prevTitle = document.title;
    document.title = `Dividelo.com – Asesoría de visa para ${cfg.demonym}`;
    const font = document.createElement("link");
    font.rel = "stylesheet";
    font.href = "https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700;800&display=swap";
    const hideChat = document.createElement("style");
    hideChat.innerHTML = "chat-widget, #lc_text-widget, .lc_text-widget, [id^='lc_text-widget'] { display: none !important; }";
    const script = document.createElement("script");
    script.src = "https://link.msgsend.com/js/form_embed.js";
    script.async = true;
    document.head.append(font, hideChat);
    document.body.appendChild(script);
    return () => {
      document.title = prevTitle;
      font.remove();
      hideChat.remove();
      script.remove();
    };
  }, [cfg.demonym]);
};

const GoldButton = ({ children, testId, className = "" }) => (
  <button
    onClick={scrollToForm}
    data-testid={testId}
    className={`inline-flex items-center gap-2 rounded-full font-semibold transition-[transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:shadow-lg ${className}`}
    style={{ background: C.gold, color: C.navy }}
  >
    {children}
  </button>
);

const Header = ({ cfg }) => (
  <header className="fixed top-0 inset-x-0 z-50" style={{ background: C.navy }} data-testid="ec-header">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 sm:h-20 flex items-center justify-between">
      <a href={cfg.path} className="flex items-center gap-2" data-testid="ec-logo">
        <span className="w-9 h-9 rounded-lg flex items-center justify-center font-bold text-lg" style={{ background: C.gold, color: C.navy }}>D</span>
        <span className="text-white font-bold text-lg sm:text-xl tracking-tight">Dividelo<span style={{ color: C.gold }}>.com</span></span>
      </a>
      <GoldButton testId="ec-header-cta" className="px-4 sm:px-6 py-2 sm:py-2.5 text-sm">Agenda tu asesoría</GoldButton>
    </div>
    <PromoStrip bonus={cfg.bonus} />
  </header>
);

const PromoStrip = ({ bonus }) => (
  <button onClick={scrollToForm} className="w-full py-2 px-4 text-center text-xs sm:text-sm font-semibold ec-promo-strip" style={{ background: C.gold, color: C.navy }} data-testid="ec-promo-strip">
    <style>{"@keyframes ecPulse{0%,100%{opacity:1}50%{opacity:.55}} .ec-pulse{animation:ecPulse 1.4s ease-in-out infinite}"}</style>
    <span className="ec-pulse mr-1.5">●</span>
    Asesorías profesionales <span className="line-through opacity-60">valor $150</span> · <strong>HOY desde $60</strong>{bonus && <> · <strong>+ {bonus.short}</strong></>} · ¡Aprovecha! →
  </button>
);

const PriceOffer = ({ bonus }) => (
  <div className="inline-flex flex-wrap items-center gap-4 sm:gap-6 rounded-2xl p-4 sm:p-5 border-2 mb-2" style={{ borderColor: C.gold, background: `${C.navy}cc` }} data-testid="ec-price-offer">
    <div>
      <p className="text-xs uppercase tracking-wider text-white/70 mb-1">Asesoría profesional</p>
      <p className="text-sm text-white/60 line-through" data-testid="ec-price-old">Valor $150</p>
      <p className="font-bold leading-none" style={{ color: C.gold }} data-testid="ec-price-new">
        <span className="text-sm align-top mr-1">HOY desde</span><span className="text-4xl sm:text-5xl">$60</span>
      </p>
    </div>
    <GoldButton testId="ec-price-cta" className="px-6 py-3 text-base">¡Aprovecha! <ArrowRight className="w-4 h-4" /></GoldButton>
    {bonus && (
      <p className="w-full flex items-center gap-2 text-sm font-semibold pt-3 border-t border-white/15" style={{ color: C.green }} data-testid="ec-price-bonus">
        <Gift className="w-5 h-5 shrink-0" /> BONO: {bonus.title}
      </p>
    )}
  </div>
);

const LeadForm = ({ formName }) => (
  <div id="ec-formulario" className="scroll-mt-36 sm:scroll-mt-40 bg-white rounded-2xl shadow-2xl p-2 sm:p-3 w-full" data-testid="ec-form-card">
    <iframe
      src={`https://api.leadconnectorhq.com/widget/form/${FORM_ID}`}
      style={{ width: "100%", height: "900px", border: "none", borderRadius: "12px" }}
      id={`inline-${FORM_ID}`}
      data-layout="{'id':'INLINE'}"
      data-trigger-type="alwaysShow"
      data-trigger-value=""
      data-activation-type="alwaysActivated"
      data-activation-value=""
      data-deactivation-type="neverDeactivate"
      data-deactivation-value=""
      data-form-name={formName}
      data-height="900"
      data-layout-iframe-id={`inline-${FORM_ID}`}
      data-form-id={FORM_ID}
      title={formName}
      data-testid="ec-ghl-form-iframe"
    />
  </div>
);

const Hero = ({ cfg }) => (
  <section className="relative pt-36 sm:pt-44 pb-16 sm:pb-24 overflow-hidden" data-testid="ec-hero">
    <img src={cfg.heroImg} alt={cfg.heroAlt} className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0" style={{ background: cfg.heroOverlay || `linear-gradient(100deg, ${C.navy} 0%, ${C.navy}e6 45%, ${C.blue}b3 100%)` }} />
    <div className="relative max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-14 items-center">
      <div className="text-white">
        <span className="inline-block text-xs sm:text-sm font-semibold tracking-wide uppercase px-3 py-1 rounded-full mb-5" style={{ background: `${C.green}33`, color: C.green }}>
          Asesoría de visa para {cfg.demonym}
        </span>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.1] mb-6" data-testid="ec-hero-title">
          Viaja a Estados Unidos, Canadá o México con una <span style={{ color: C.gold }}>solicitud bien preparada</span>
        </h1>
        <p className="text-base sm:text-lg text-white/85 max-w-xl mb-8">
          Te ayudamos a entender qué visa necesitas, cómo presentar tu perfil y qué documentos preparar. Empieza con una reunión de 20 minutos con un asesor.
        </p>
        <ul className="space-y-3 mb-8">
          {["Reunión por videollamada de 20 minutos", "Plan de acción claro y personalizado", "Atención 100% en español"].map((t) => (
            <li key={t} className="flex items-center gap-3 text-sm sm:text-base"><CheckCircle2 className="w-5 h-5 shrink-0" style={{ color: C.green }} />{t}</li>
          ))}
        </ul>
        <PriceOffer bonus={cfg.bonus} />
      </div>
      <LeadForm formName={cfg.formName} />
    </div>
  </section>
);

const ADVANCE_IMG = "https://customer-assets-m6fa6gv7.emergentagent.net/job_6b122577-9945-4679-9acb-e5453229c54e/artifacts/c4fc44a33d1c77e9_kit-formerly-convertkit-htQznS-Rx7w-unsplash.webp";

const ADVANCE_POINTS = [
  { title: "No solo llenamos un formulario", desc: "Construimos tu perfil completo: trabajo, familia, finanzas, viajes y arraigo, presentados con coherencia." },
  { title: "Te damos tu porcentaje real", desc: "Antes de que pagues tasas consulares, te decimos qué tan cerca estás de una aprobación o de una negación." },
  { title: "No te hacemos gastar por gusto", desc: "Si tu perfil aún no está listo, lo mejoramos contigo y te decimos exactamente cuándo postular." },
];

const VERDICTS = [
  { label: "Sí", desc: "Perfil sólido, es momento de aplicar", color: C.green },
  { label: "Tal vez", desc: "Hay que reforzar puntos clave", color: C.gold },
  { label: "No", desc: "Mejor esperar y prepararlo bien", color: "#e8836b" },
];

const ProfileMeter = () => (
  <div className="absolute -bottom-8 right-4 sm:right-8 w-[260px] sm:w-[300px] bg-white rounded-2xl shadow-2xl p-5" data-testid="ec-profile-meter">
    <div className="flex justify-between items-center mb-1">
      <span className="text-xs font-semibold uppercase tracking-wide" style={{ color: C.blue }}>Tu perfil</span>
      <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-black/5">Ejemplo</span>
    </div>
    <p className="text-3xl font-bold mb-3" style={{ color: C.navy }}>82%</p>
    <div className="h-2.5 rounded-full bg-black/10 overflow-hidden mb-2">
      <div className="h-full rounded-full ec-meter-fill" style={{ width: "82%", background: `linear-gradient(90deg, ${C.gold}, ${C.green})` }} />
    </div>
    <p className="text-xs opacity-70">Probabilidad estimada de aprobación</p>
  </div>
);

const Stat = ({ value, label, testId }) => (
  <div data-testid={testId}>
    <p className="text-4xl sm:text-5xl font-bold" style={{ color: C.gold }}>{value}</p>
    <p className="text-sm text-white/75 mt-1">{label}</p>
  </div>
);

const AdvanceSystem = () => (
  <section className="py-20 sm:py-28 bg-white overflow-hidden" data-testid="ec-advance-section">
    <style>{"@keyframes ecFill{from{width:0}} .ec-meter-fill{animation:ecFill 1.6s ease-out}"}</style>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-14 lg:gap-16 items-center">
      <div className="relative">
        <img src={ADVANCE_IMG} alt="Pasaporte abierto con sellos de viaje" className="w-full h-[340px] sm:h-[480px] object-cover rounded-3xl shadow-xl" data-testid="ec-advance-image" />
        <span className="absolute top-5 left-5 text-xs sm:text-sm font-bold px-4 py-2 rounded-full shadow-lg" style={{ background: C.gold, color: C.navy }} data-testid="ec-advance-badge">100% garantizado</span>
        <ProfileMeter />
      </div>
      <div className="pt-6 lg:pt-0">
        <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Sistema de adelanto de cita</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-5 leading-tight" style={{ color: C.navy }} data-testid="ec-advance-title">
          Adelantamos tu cita con un formato <span className="underline decoration-4 underline-offset-4" style={{ textDecorationColor: C.gold }}>100% garantizado</span>
        </h2>
        <p className="text-base sm:text-lg opacity-80 mb-8" data-testid="ec-advance-guarantee">
          Si no logramos adelantar tu cita a la primera, seguimos intentando sin costo extra hasta conseguirlo.
        </p>
        <ul className="space-y-5 mb-9">
          {ADVANCE_POINTS.map((p, i) => (
            <li key={p.title} className="flex gap-4" data-testid={`ec-advance-point-${i + 1}`}>
              <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: C.green }} />
              <div>
                <h3 className="font-semibold" style={{ color: C.navy }}>{p.title}</h3>
                <p className="text-sm sm:text-base opacity-80">{p.desc}</p>
              </div>
            </li>
          ))}
        </ul>
        <GoldButton testId="ec-advance-cta" className="px-7 py-3 text-base">Quiero adelantar mi cita <ArrowRight className="w-4 h-4" /></GoldButton>
      </div>
    </div>
    <div className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
      <div className="rounded-3xl p-8 sm:p-12 text-white grid lg:grid-cols-[1fr_1.4fr] gap-10 items-center" style={{ background: C.navy }} data-testid="ec-stats-block">
        <div>
          <div className="flex gap-10 mb-6">
            <Stat value="95%" label="de aprobación en nuestros clientes" testId="ec-stat-approval" />
            <Stat value="17" label="años asesorando viajeros" testId="ec-stat-years" />
          </div>
          <p className="text-sm sm:text-base text-white/80">
            Nuestro 95% no es suerte: son 17 años revisando perfiles. Sabemos reconocer cuándo un caso es un sí, cuándo es un no y cuándo es un tal vez.
          </p>
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          {VERDICTS.map((v) => (
            <div key={v.label} className="rounded-2xl p-5" style={{ background: C.blue }} data-testid={`ec-verdict-${v.label.toLowerCase().replace(" ", "-")}`}>
              <span className="inline-block w-3 h-3 rounded-full mb-3" style={{ background: v.color }} />
              <p className="text-xl font-bold mb-1" style={{ color: v.color }}>{v.label}</p>
              <p className="text-sm text-white/75">{v.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Meeting = ({ city }) => (
  <section className="py-20 sm:py-28" style={{ background: C.bg }} data-testid="ec-meeting-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Cómo empezamos</p>
      <h2 className="text-3xl sm:text-4xl font-bold mb-4" style={{ color: C.navy }}>Reunión de 20 minutos</h2>
      <p className="text-base sm:text-lg max-w-2xl mb-14 opacity-80">Una conversación corta y directa para entender tu caso y decirte, con claridad, cuál es el mejor camino.</p>
      <div className="grid md:grid-cols-3 gap-6">
        {steps(city).map((s, i) => (
          <div key={s.title} className="relative bg-white rounded-2xl p-7 sm:p-8 shadow-sm border border-black/5 transition-transform duration-200 hover:-translate-y-1" data-testid={`ec-step-${i + 1}`}>
            <span className="absolute -top-5 left-7 w-11 h-11 rounded-full flex items-center justify-center font-bold text-lg shadow" style={{ background: C.gold, color: C.navy }}>{i + 1}</span>
            <s.icon className="w-7 h-7 mt-4 mb-4" style={{ color: C.blue }} />
            <h3 className="text-lg font-semibold mb-2" style={{ color: C.navy }}>{s.title}</h3>
            <p className="text-sm sm:text-base opacity-80 leading-relaxed">{s.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const Destinations = () => (
  <section className="py-20 sm:py-28 bg-white" data-testid="ec-destinations-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8">
      <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: C.blue }}>Destinos</p>
      <h2 className="text-3xl sm:text-4xl font-bold mb-14" style={{ color: C.navy }}>¿A dónde quieres viajar?</h2>
      <div className="grid md:grid-cols-3 gap-6">
        {DESTINATIONS.map((d) => (
          <article key={d.name} className="group rounded-2xl overflow-hidden border border-black/5 shadow-sm bg-white flex flex-col" data-testid={`ec-destination-${d.name.toLowerCase().replace(/\s+/g, "-").normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}>
            <div className="h-52 overflow-hidden">
              <img src={d.img} alt={d.name} style={{ objectPosition: d.pos || "center" }} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
            </div>
            <div className="p-7 flex flex-col flex-1">
              <span className="self-start text-xs font-semibold px-3 py-1 rounded-full mb-3" style={{ background: `${C.green}55`, color: C.navy }}>{d.visa}</span>
              <h3 className="text-xl font-bold mb-2" style={{ color: C.navy }}>{d.name}</h3>
              <p className="text-sm sm:text-base opacity-80 mb-6 flex-1">{d.desc}</p>
              <button onClick={scrollToForm} className="inline-flex items-center gap-2 text-sm font-semibold transition-[gap] duration-200 hover:gap-3" style={{ color: C.blue }} data-testid={`ec-destination-cta-${d.name.length}`}>
                Quiero asesoría <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  </section>
);

const WhyItWorks = () => (
  <section className="py-20 sm:py-28 text-white" style={{ background: C.navy }} data-testid="ec-why-section">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 grid lg:grid-cols-2 gap-12 items-center">
      <div>
        <p className="text-sm font-semibold uppercase tracking-wider mb-3" style={{ color: C.gold }}>Nuestro enfoque</p>
        <h2 className="text-3xl sm:text-4xl font-bold mb-5">Por qué funciona</h2>
        <p className="text-base sm:text-lg text-white/75 mb-8 max-w-lg">Una solicitud de visa no se trata solo de llenar formularios: se trata de presentar tu historia de forma clara, coherente y respaldada.</p>
        <GoldButton testId="ec-why-cta" className="px-7 py-3 text-base">Agenda tu asesoría <ArrowRight className="w-4 h-4" /></GoldButton>
      </div>
      <ul className="space-y-4">
        {REASONS.map((r, i) => (
          <li key={r} className="flex gap-4 rounded-xl p-5" style={{ background: C.blue }} data-testid={`ec-reason-${i + 1}`}>
            <CheckCircle2 className="w-6 h-6 shrink-0 mt-0.5" style={{ color: C.green }} />
            <span className="text-sm sm:text-base text-white/90">{r}</span>
          </li>
        ))}
      </ul>
    </div>
  </section>
);

const LegalNotice = () => (
  <section className="py-14" style={{ background: C.bg }} data-testid="ec-legal-section">
    <div className="max-w-4xl mx-5 sm:mx-auto flex gap-4 items-start bg-white rounded-2xl p-6 sm:p-8 border border-black/5">
      <ShieldAlert className="w-7 h-7 shrink-0" style={{ color: C.blue }} />
      <div>
        <h2 className="text-lg font-semibold mb-2" style={{ color: C.navy }}>Aviso legal</h2>
        <p className="text-sm leading-relaxed opacity-80" data-testid="ec-legal-text">
          Dividelo.com es un servicio privado de asesoría y no es una agencia gubernamental ni está afiliado a ninguna embajada o consulado. No garantizamos la aprobación de ninguna visa: la decisión final corresponde exclusivamente a las autoridades consulares de cada país. El 95% de aprobación corresponde a nuestro historial de clientes y el porcentaje de perfil es una estimación orientativa. La garantía aplica al adelanto de cita, no a la aprobación de la visa. Las tasas consulares oficiales no están incluidas en nuestros honorarios y se pagan directamente a la entidad correspondiente.
        </p>
      </div>
    </div>
  </section>
);

const Footer = ({ demonym }) => (
  <footer className="py-10 text-white/70" style={{ background: C.navy }} data-testid="ec-footer">
    <div className="max-w-7xl mx-auto px-5 sm:px-8 flex flex-col sm:flex-row gap-4 justify-between items-center text-sm">
      <span>© {new Date().getFullYear()} Dividelo.com – Asesoría de visa para {demonym}</span>
      <div className="flex items-center gap-5">
        <a href="mailto:info@clubviajemos.com" className="flex items-center gap-1.5 hover:text-white transition-colors" data-testid="ec-footer-email"><Mail className="w-4 h-4" />info@clubviajemos.com</a>
        <a href="/privacidad" className="hover:text-white transition-colors" data-testid="ec-footer-privacy">Privacidad</a>
        <a href="/terminos" className="hover:text-white transition-colors" data-testid="ec-footer-terms">Términos</a>
      </div>
    </div>
  </footer>
);

const BonusSection = ({ bonus }) => (
  <section className="py-16 sm:py-20 bg-white" data-testid="ec-bonus-section">
    <div className="max-w-7xl mx-5 sm:mx-8 xl:mx-auto relative rounded-3xl overflow-hidden">
      <img src={bonus.img} alt="Miami Beach" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0" style={{ background: `linear-gradient(90deg, ${C.navy} 0%, ${C.navy}d9 50%, ${C.navy}40 100%)` }} />
      <div className="relative p-8 sm:p-14 max-w-2xl text-white">
        <span className="inline-flex items-center gap-2 text-xs sm:text-sm font-bold uppercase tracking-wider px-4 py-1.5 rounded-full mb-5" style={{ background: C.gold, color: C.navy }}>
          <Gift className="w-4 h-4" /> Bono de regalo
        </span>
        <h2 className="text-3xl sm:text-5xl font-bold leading-tight mb-4" data-testid="ec-bonus-title">
          3 días y 2 noches en <span style={{ color: C.gold }}>Miami Beach</span> para 2 personas <span style={{ color: C.green }}>GRATIS</span>
        </h2>
        <p className="text-base sm:text-lg text-white/85 mb-8">Contrata hoy tu asesoría profesional desde $60 y llévate de regalo una escapada a Miami Beach para ti y tu acompañante.</p>
        <GoldButton testId="ec-bonus-cta" className="px-7 py-3 text-base">Quiero mi bono <ArrowRight className="w-4 h-4" /></GoldButton>
        <p className="text-xs text-white/60 mt-5" data-testid="ec-bonus-terms">Bono sujeto a términos y condiciones. No incluye pasajes aéreos ni visa.</p>
      </div>
    </div>
  </section>
);

export const CountryLanding = ({ cfg }) => {
  usePageSetup(cfg);
  return (
    <div style={{ fontFamily: "'Poppins', sans-serif", color: C.text, background: C.bg }} data-testid={`${cfg.path.slice(1)}-landing-page`}>
      <Header cfg={cfg} />
      <Hero cfg={cfg} />
      {cfg.bonus && <BonusSection bonus={cfg.bonus} />}
      <AdvanceSystem />
      <Meeting city={cfg.city} />
      <Destinations />
      <WhyItWorks />
      <LegalNotice />
      <Footer demonym={cfg.demonym} />
    </div>
  );
};

export default function EcuadorLandingPage() {
  return <CountryLanding cfg={ECUADOR} />;
}
